import AgoraRTM from 'agora-rtm-sdk'
import { getAgoraSignalingToken } from 'src/consumer/services/featureServices'
import { SIGNALING_PROVIDERS } from '../../../providerIds'

function createEventBus() {
  const listeners = new Map()

  return {
    emit(eventName, payload) {
      const handlers = listeners.get(eventName)

      if (!handlers) {
        return
      }

      handlers.forEach((handler) => handler(payload))
    },
    on(eventName, handler) {
      const handlers = listeners.get(eventName) || new Set()
      handlers.add(handler)
      listeners.set(eventName, handlers)

      return () => {
        const nextHandlers = listeners.get(eventName)

        if (!nextHandlers) {
          return
        }

        nextHandlers.delete(handler)

        if (nextHandlers.size === 0) {
          listeners.delete(eventName)
        }
      }
    },
    clear() {
      listeners.clear()
    },
  }
}

function createLogger(enableLogging = true) {
  const noop = () => {}

  if (!enableLogging) {
    return {
      debug: noop,
      info: noop,
      warn: noop,
      error: noop,
    }
  }

  return {
    debug: (...args) => console.log(...args),
    info: (...args) => console.info(...args),
    warn: (...args) => console.warn(...args),
    error: (...args) => console.error(...args),
  }
}

function createSignalingError(message, cause = null) {
  const error = new Error(message)

  if (cause) {
    error.cause = cause
  }

  return error
}

function isWebSocketClosingError(error) {
  return error?.message?.includes('CLOSING') || error?.message?.includes('CLOSED')
}

async function resolveSignalingToken({ tokenProvider, deviceId }) {
  const response = await tokenProvider(deviceId)

  if (response?.isAxiosError || response?.response) {
    throw createSignalingError(
      'Failed to fetch Agora signaling token',
      response?.response?.data || response,
    )
  }

  if (response?.status !== 200) {
    throw createSignalingError(
      `Unexpected Agora signaling token response status: ${response?.status || 'unknown'}`,
      response,
    )
  }

  const token = response?.data

  if (typeof token !== 'string' || token.trim() === '') {
    throw createSignalingError('Invalid Agora signaling token received from server', response)
  }

  return token
}

export const SIGNALING_CONNECTION_STATUSES = Object.freeze({
  disconnected: 'disconnected',
  disconnecting: 'disconnecting',
  initializing: 'initializing',
  connecting: 'connecting',
  connected: 'connected',
  refreshing: 'refreshing',
  kickedOff: 'kicked_off',
  failed: 'failed',
})

export const SIGNALING_SESSION_EVENTS = Object.freeze({
  message: 'message',
  connectionStatusChanged: 'connection-status-changed',
  connectionStateChanged: 'connection-state-changed',
  error: 'error',
  tokenWillExpire: 'token-will-expire',
})

const AGORA_SIGNALING_ADAPTER_CAPABILITIES = Object.freeze({
  peerMessageReceive: true,
  tokenRefresh: true,
  connectionStatusEvents: true,
  windowEventDispatchBridge: true,
})

export function createAgoraSignalingAdapterCapabilities(overrides = {}) {
  return {
    ...AGORA_SIGNALING_ADAPTER_CAPABILITIES,
    ...(overrides && typeof overrides === 'object' ? overrides : {}),
  }
}

export function createAgoraSignalingConnection({
  appId = import.meta.env.VITE_AGORA_APP_ID,
  deviceId,
  enableLogging = true,
  tokenProvider = getAgoraSignalingToken,
} = {}) {
  if (!deviceId) {
    throw new Error('[live/signaling/agora] deviceId is required')
  }

  const log = createLogger(enableLogging)
  const events = createEventBus()
  let client = null
  let destroyed = false
  let connectPromise = null
  let connectionStatus = SIGNALING_CONNECTION_STATUSES.disconnected
  let currentConnectionState = null
  let currentClientListeners = null

  function assertActive(methodName) {
    if (!destroyed) {
      return
    }

    throw new Error(`[live/signaling/agora] ${methodName}() called after destroy()`)
  }

  function emitError(error, context = {}) {
    events.emit(SIGNALING_SESSION_EVENTS.error, {
      error,
      context,
      deviceId,
      providerId: SIGNALING_PROVIDERS.agoraRtm,
    })
  }

  function updateConnectionStatus(status, context = {}) {
    connectionStatus = status
    events.emit(SIGNALING_SESSION_EVENTS.connectionStatusChanged, {
      status,
      ...context,
      deviceId,
      providerId: SIGNALING_PROVIDERS.agoraRtm,
    })
  }

  function registerClientListeners(activeClient) {
    const messageHandler = (event) => {
      if (!event?.message) {
        log.warn('[AGORA RTM] Received message event without message property')
        return
      }

      try {
        const parsedMessage = JSON.parse(event.message)
        const command = parsedMessage?.command

        if (!command) {
          log.warn('[AGORA RTM] Received signaling message without command', parsedMessage)
          return
        }

        events.emit(SIGNALING_SESSION_EVENTS.message, {
          message: parsedMessage,
          rawMessage: event.message,
          deviceId,
          providerId: SIGNALING_PROVIDERS.agoraRtm,
        })
      } catch (error) {
        log.error('[AGORA RTM] Failed to parse signaling message', error)
        emitError(error, {
          phase: 'message-parse',
          rawMessage: event?.message,
        })
      }
    }

    const connectionStateHandler = (newState, reason) => {
      currentConnectionState = newState

      events.emit(SIGNALING_SESSION_EVENTS.connectionStateChanged, {
        state: newState,
        reason,
        deviceId,
        providerId: SIGNALING_PROVIDERS.agoraRtm,
      })

      if (newState === 'DISCONNECTED' || newState === 'ABORTED') {
        if (reason === 'REMOTE_LOGIN' || reason === 'KICKED_BY_REMOTE') {
          updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.kickedOff, { reason })
        } else if (!destroyed) {
          updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.disconnected, { reason })
        }
        return
      }

      if (newState === 'CONNECTING') {
        updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.connecting, { reason })
        return
      }

      if (newState === 'CONNECTED') {
        updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.connected, { reason })
      }
    }

    const errorHandler = (error) => {
      if (error?.code === 101 || error?.message?.includes('Database connection failed')) {
        log.warn('[AGORA RTM] Database sync connection error (101); allowing SDK retry flow')
        return
      }

      log.error('[AGORA RTM] Signaling client error', error)
      emitError(error, {
        phase: 'client-error',
      })
    }

    const tokenExpiryHandler = async () => {
      updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.refreshing)
      events.emit(SIGNALING_SESSION_EVENTS.tokenWillExpire, {
        deviceId,
        providerId: SIGNALING_PROVIDERS.agoraRtm,
      })

      try {
        const token = await resolveSignalingToken({
          tokenProvider,
          deviceId,
        })
        await activeClient.renewToken(token)
        updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.connected)
      } catch (error) {
        log.error('[AGORA RTM] Failed to renew signaling token', error)
        updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.failed)
        emitError(error, {
          phase: 'token-renew',
        })
      }
    }

    activeClient.addEventListener('message', messageHandler)
    activeClient.on?.('ConnectionStateChanged', connectionStateHandler)
    activeClient.on?.('error', errorHandler)
    activeClient.addEventListener?.('tokenPrivilegeWillExpire', tokenExpiryHandler)

    currentClientListeners = {
      messageHandler,
      connectionStateHandler,
      errorHandler,
      tokenExpiryHandler,
    }
  }

  function unregisterClientListeners(activeClient) {
    if (!activeClient || !currentClientListeners) {
      return
    }

    try {
      activeClient.removeEventListener?.('message', currentClientListeners.messageHandler)
    } catch (error) {
      log.warn('[AGORA RTM] Failed to remove message listener', error)
    }

    activeClient.off?.('ConnectionStateChanged', currentClientListeners.connectionStateHandler)
    activeClient.off?.('error', currentClientListeners.errorHandler)

    try {
      activeClient.removeEventListener?.(
        'tokenPrivilegeWillExpire',
        currentClientListeners.tokenExpiryHandler,
      )
    } catch (error) {
      log.warn('[AGORA RTM] Failed to remove token-expiry listener', error)
    }

    currentClientListeners = null
  }

  async function logoutClient(activeClient) {
    if (!activeClient) {
      return
    }

    const connectionState = activeClient.getConnectionState?.() || currentConnectionState

    if (connectionState === 'DISCONNECTED' || connectionState === 'ABORTED') {
      return
    }

    try {
      await activeClient.logout()
    } catch (error) {
      if (isWebSocketClosingError(error)) {
        return
      }

      throw error
    }
  }

  async function connect() {
    assertActive('connect')

    if (client && connectionStatus === SIGNALING_CONNECTION_STATUSES.connected) {
      return handle
    }

    if (connectPromise) {
      return connectPromise
    }

    connectPromise = (async () => {
      try {
        if (client) {
          await disconnect()
        }

        updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.initializing)
        client = await new AgoraRTM.RTM(appId, deviceId)
        registerClientListeners(client)

        updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.connecting)
        const token = await resolveSignalingToken({
          tokenProvider,
          deviceId,
        })

        if (destroyed) {
          await logoutClient(client)
          return handle
        }

        await client.login({ token })

        if (!destroyed) {
          updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.connected)
        }

        return handle
      } catch (error) {
        const failedClient = client

        if (failedClient) {
          unregisterClientListeners(failedClient)

          try {
            await logoutClient(failedClient)
          } catch (logoutError) {
            if (!isWebSocketClosingError(logoutError)) {
              log.warn('[AGORA RTM] Error while cleaning up failed connection', logoutError)
            }
          }

          if (client === failedClient) {
            client = null
          }

          currentConnectionState = 'DISCONNECTED'
        }

        if (!destroyed) {
          log.error('[AGORA RTM] Signaling connection failed', error)
          updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.failed)
          emitError(error, {
            phase: 'connect',
          })
        }

        throw error
      } finally {
        connectPromise = null
      }
    })()

    return connectPromise
  }

  async function disconnect() {
    if (!client) {
      updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.disconnected)
      return
    }

    updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.disconnecting)

    const activeClient = client
    unregisterClientListeners(activeClient)

    try {
      await logoutClient(activeClient)
    } catch (error) {
      if (!isWebSocketClosingError(error)) {
        log.warn('[AGORA RTM] Error while logging out during disconnect', error)
      }
    } finally {
      if (client === activeClient) {
        client = null
      }
      currentConnectionState = 'DISCONNECTED'
      updateConnectionStatus(SIGNALING_CONNECTION_STATUSES.disconnected)
    }
  }

  async function destroy() {
    if (destroyed) {
      return
    }

    destroyed = true
    await disconnect()
    events.clear()
  }

  const handle = {
    providerId: SIGNALING_PROVIDERS.agoraRtm,
    getClient() {
      return client
    },
    getConfig() {
      return {
        appId,
        deviceId,
        enableLogging,
      }
    },
    getConnectionStatus() {
      return connectionStatus
    },
    getConnectionState() {
      return currentConnectionState
    },
    on(eventName, handler) {
      assertActive('on')
      return events.on(eventName, handler)
    },
    connect,
    disconnect,
    destroy,
  }

  return handle
}

export async function connectAgoraSignaling(config = {}) {
  const handle = createAgoraSignalingConnection(config)
  await handle.connect()
  return handle
}

export function createAgoraSignalingAdapter({ capabilityOverrides = {} } = {}) {
  return {
    id: SIGNALING_PROVIDERS.agoraRtm,
    domain: 'signaling',
    transport: 'agora-rtm',
    supports: createAgoraSignalingAdapterCapabilities(capabilityOverrides),
    createConnection(initialConfig = {}) {
      return createAgoraSignalingConnection(initialConfig)
    },
    connect: connectAgoraSignaling,
  }
}

export const agoraSignalingAdapter = createAgoraSignalingAdapter()
